'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/app/utils/cn';
import { CallToActionButton } from '@/components/shared/CallToActionButton';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';

function ListItem({ children }) {
    return <li className="list-disc">{children}</li>;
}

const HEADING_STYLES = {
    1: 'mb-3 mt-6 text-4xl font-bold first:mt-0',
    2: 'mb-3 mt-5 text-3xl font-bold first:mt-0',
    3: 'mb-2 mt-4 text-2xl font-bold first:mt-0',
    4: 'mb-2 mt-3 text-xl font-semibold first:mt-0',
    5: 'mb-2 mt-3 text-lg font-semibold first:mt-0',
    6: 'mb-2 mt-2 text-base font-semibold first:mt-0'
};

function Heading({ node, children }) {
    const level = node?.attrs?.level || 3;
    const textAlign = node?.attrs?.textAlign;
    const HeadingTag = `h${level}`;
    return (
        <HeadingTag
            className={HEADING_STYLES[level] ?? HEADING_STYLES[3]}
            style={textAlign ? { textAlign } : undefined}>
            {children}
        </HeadingTag>
    );
}

function SlideBody({ body, className }) {
    if (!body) return null;
    if (typeof body === 'string') {
        return <p className={className}>{body}</p>;
    }
    return (
        <div className={className}>
            <DotBlockEditor
                blocks={body}
                customRenderers={{ listItem: ListItem, heading: Heading }}
            />
        </div>
    );
}

const TEXT_HEIGHT = 380;

function SlideCTAs({ ctas }) {
    if (!ctas?.length) return null;
    return (
        <div className="mt-8 flex flex-wrap items-center gap-5">
            {ctas.map((cta, i) => (
                <CallToActionButton
                    key={i}
                    buttonType={cta.buttonType || 'primary'}
                    url={cta.url}
                    urlName={cta.urlName}
                    ariaLabel={cta.ariaLabel}
                    position={cta.position || 'left'}
                    iconPosition={cta.iconPosition}
                    extraSpace={cta.extraSpace}
                    className={'m-0 h-full'}>
                    {cta.urlName}
                </CallToActionButton>
            ))}
        </div>
    );
}

function MobileList({ eyebrow, heading, slides }) {
    return (
        <div className="md:hidden">
            <div className="mx-auto max-w-3xl px-6 py-16">
                {(eyebrow || heading) && (
                    <header className="mb-10">
                        {eyebrow && (
                            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-purple-600">
                                {eyebrow}
                            </p>
                        )}
                        {heading && <h2 className="text-3xl font-bold text-blue-700">{heading}</h2>}
                    </header>
                )}
                <ul className="space-y-16">
                    {slides.map((slide, i) => (
                        <li key={i} className="space-y-6">
                            {slide.image?.src && (
                                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={slide.image.src}
                                        alt={slide.image.alt || ''}
                                        className="absolute inset-0 size-full object-cover"
                                    />
                                </div>
                            )}
                            <div>
                                <h3 className="mb-3 text-2xl font-bold text-blue-700">
                                    {slide.title}
                                </h3>
                                <SlideBody
                                    body={slide.body}
                                    className="text-base leading-relaxed text-gray-600"
                                />
                                <SlideCTAs ctas={slide.ctas} />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function FreeVariant({ slides }) {
    const blockRefs = useRef([]);
    const [activeIdx, setActiveIdx] = useState(0);

    // The site-wide `body { overflow: hidden }` in globals.css makes body a
    // non-scrolling scroll container, which breaks `position: sticky` inside
    // it. Override to visible while this component is mounted.
    useEffect(() => {
        document.body.style.setProperty('overflow', 'visible', 'important');
        return () => {
            document.body.style.removeProperty('overflow');
        };
    }, []);

    useEffect(() => {
        if (!slides.length) return;
        let rafId = null;
        let pending = false;

        const compute = () => {
            pending = false;
            const vh = window.innerHeight;
            const targetY = vh / 2;
            let bestIdx = 0;
            let bestDist = Infinity;
            for (let i = 0; i < blockRefs.current.length; i++) {
                const b = blockRefs.current[i];
                if (!b) continue;
                const r = b.getBoundingClientRect();
                const center = r.top + r.height / 2;
                const dist = Math.abs(center - targetY);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestIdx = i;
                }
            }
            setActiveIdx(bestIdx);
        };

        const onScroll = () => {
            if (pending) return;
            pending = true;
            rafId = requestAnimationFrame(compute);
        };

        compute();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [slides.length]);

    return (
        <div className="hidden md:block">
            <div className="px-8 lg:px-16">
                <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-16">
                    <div>
                        {/* min-h (not h) — long-content slides grow past 70vh instead of
                            overflowing into adjacent slides on short viewports (e.g.
                            1366×768, where 70vh ≈ 538px is smaller than a full slide). */}
                        {slides.map((slide, i) => (
                            <article
                                key={i}
                                ref={(el) => (blockRefs.current[i] = el)}
                                className="flex min-h-[70vh] flex-col items-center justify-center py-12">
                                <div className="w-full max-w-xl">
                                    <span className="mb-3 inline-block font-mono text-sm text-gray-400">
                                        {slide.label ||
                                            `${String(i + 1).padStart(2, '0')} / ${String(
                                                slides.length
                                            ).padStart(2, '0')}`}
                                    </span>
                                    <h3 className="mb-4 text-4xl font-bold text-blue-700">
                                        {slide.title}
                                    </h3>
                                    <SlideBody
                                        body={slide.body}
                                        className="text-lg leading-relaxed text-gray-600"
                                    />
                                    <SlideCTAs ctas={slide.ctas} />
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* Image column — the grid row's default align-items: stretch makes
                        this cell match the text column's actual height, so the sticky
                        image stays pinned across the whole section even when individual
                        slides grow past 70vh. */}
                    <div>
                        <div className="sticky top-[15vh] flex h-[70vh] items-center">
                            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl">
                                {slides.map((slide, i) =>
                                    slide.image?.src ? (
                                        <div
                                            key={i}
                                            className="absolute inset-0 transition-opacity duration-500"
                                            style={{ opacity: i === activeIdx ? 1 : 0 }}>
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={slide.image.src}
                                                alt={slide.image.alt || ''}
                                                className="absolute inset-0 size-full object-cover"
                                            />
                                        </div>
                                    ) : null
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PagedVariant({ slides, eyebrow, heading }) {
    const sectionRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    // Vertical offset for the sticky content, in vh. Starts near section top so
    // the content feels integrated with what's above it, then shifts down to
    // viewport center as the user scrolls into the section.
    const [entryOffsetVh, setEntryOffsetVh] = useState(0);

    // The site-wide `body { overflow: hidden }` in globals.css makes body a
    // non-scrolling scroll container, which breaks `position: sticky` inside
    // it. Override to visible while this component is mounted.
    useEffect(() => {
        document.body.style.setProperty('overflow', 'visible', 'important');
        return () => {
            document.body.style.removeProperty('overflow');
        };
    }, []);

    useEffect(() => {
        if (!slides.length) return;
        const el = sectionRef.current;
        if (!el) return;

        let rafId = null;
        let pending = false;
        let lastIdx = -1;

        const update = () => {
            pending = false;
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight;
            const total = rect.height - vh;
            if (total <= 0) return;

            const scrolled = Math.max(0, Math.min(total, -rect.top));
            const progress = scrolled / total;
            const idx = Math.min(slides.length - 1, Math.floor(progress * slides.length));

            if (idx !== lastIdx) {
                lastIdx = idx;
                setActiveIndex(idx);
            }

            // Entry progress: 0 when section top is at viewport bottom (just
            // entering view), 1 when section top reaches viewport top (sticky
            // pins). We lift the content UP by up to 35vh on entry so it sits
            // near the top of the sticky container (integrated with content
            // above), then settle to natural items-center position at pin.
            const entry = Math.max(0, Math.min(1, (vh - rect.top) / vh));
            setEntryOffsetVh((entry - 1) * 35);
        };

        const onScroll = () => {
            if (pending) return;
            pending = true;
            rafId = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [slides.length]);

    return (
        <div
            ref={sectionRef}
            className="relative hidden md:block"
            style={{ height: `${slides.length * 70}vh` }}>
            <div className="sticky top-0 flex h-screen w-full items-center justify-center pt-20">
                <div
                    className="flex w-full items-center gap-8 px-8 will-change-transform"
                    style={{
                        transform: `translateY(${entryOffsetVh}vh)`,
                        transition: 'transform 200ms ease-out'
                    }}>
                    {/* Left: text column with sliding filmstrip */}
                    <div className="min-w-0 flex-1">
                        {(eyebrow || heading) && (
                            <header className="mb-8">
                                {eyebrow && (
                                    <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-purple-600">
                                        {eyebrow}
                                    </p>
                                )}
                                {heading && (
                                    <h2 className="text-4xl font-bold text-blue-700">{heading}</h2>
                                )}
                            </header>
                        )}
                        <div
                            className="relative overflow-hidden"
                            style={{ height: `${TEXT_HEIGHT}px` }}>
                            <div
                                className="flex flex-col will-change-transform"
                                style={{
                                    transform: `translateY(-${activeIndex * TEXT_HEIGHT}px)`,
                                    transition: 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1)'
                                }}>
                                {slides.map((slide, i) => (
                                    <article
                                        key={i}
                                        className="flex flex-col justify-center"
                                        style={{ height: `${TEXT_HEIGHT}px` }}
                                        aria-hidden={i !== activeIndex}>
                                        <span className="mb-3 inline-block font-mono text-sm text-gray-400">
                                            {slide.label ||
                                                `${String(i + 1).padStart(2, '0')} / ${String(
                                                    slides.length
                                                ).padStart(2, '0')}`}
                                        </span>
                                        <h3 className="mb-4 text-4xl font-bold text-blue-700">
                                            {slide.title}
                                        </h3>
                                        <SlideBody
                                            body={slide.body}
                                            className="text-lg leading-relaxed text-gray-600"
                                        />
                                        <SlideCTAs ctas={slide.ctas} />
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Vertical indicator */}
                    <ol className="flex flex-col gap-3 py-4" aria-hidden>
                        {slides.map((_, i) => (
                            <li
                                key={i}
                                className={cn(
                                    'w-1 rounded-full transition-all duration-500',
                                    i === activeIndex ? 'h-10 bg-purple-600' : 'h-2 bg-gray-300'
                                )}
                            />
                        ))}
                    </ol>

                    {/* Right: image column with crossfade */}
                    <div className="relative aspect-[4/3] min-w-0 flex-1 overflow-hidden rounded-3xl">
                        {slides.map((slide, i) =>
                            slide.image?.src ? (
                                <div
                                    key={i}
                                    aria-hidden={i !== activeIndex}
                                    className={cn(
                                        'absolute inset-0 transition-opacity duration-700 ease-out',
                                        i === activeIndex ? 'opacity-100' : 'opacity-0'
                                    )}>
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={slide.image.src}
                                        alt={slide.image.alt || ''}
                                        className="absolute inset-0 size-full object-cover"
                                    />
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScrollableGrid({ type = 'free', slides = [], eyebrow, heading }) {
    if (!slides.length) return null;

    return (
        <section className="relative w-full">
            <MobileList eyebrow={eyebrow} heading={heading} slides={slides} />
            {type === 'paged' ? (
                <PagedVariant slides={slides} eyebrow={eyebrow} heading={heading} />
            ) : (
                <FreeVariant slides={slides} />
            )}
        </section>
    );
}

export default ScrollableGrid;
